<template>
  <div class="message-center pb-20">
    <div v-if="!messages.length">
      <div class="text-center italic">No Alert Messages</div>
    </div>

    <template v-else>
      <div class="flex justify-end self-end">
        <KaButton
          id="muteAllAlerts"
          data-testid="mute-all-alerts"
          type="button"
          size="small"
          variant="link"
          class="mb-6"
          @click="onMuteAll(!allMuted)"
        >
          {{ allMuted ? 'unmute all' : 'mute all' }}
        </KaButton>
      </div>

      <transition-group name="scaleup" tag="ul" class="alerts-list m-0 grid list-none gap-6 p-0">
        <AlertMessageCard
          v-for="alert in messages"
          :key="alert.id"
          :alert="alert"
          @muteClick="onMuteClick(alert.id)"
          @buttonClick="onButtonClick(alert)"
        />
      </transition-group>
    </template>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import localStore from 'store';

import { checkAll, localStoreKey } from '@/alert-checks';
import { dashboardBffClient } from '@/api-client';
import AlertMessageCard from '@/components/AlertMessageCard';
import { KaButton } from '@/kasten-components';
import * as log from '@/logger';
import { useMainStore } from '@/stores/main';

export default {
  name: 'AlertMessageList',
  components: { AlertMessageCard, KaButton },
  emits: ['close'],
  computed: {
    ...mapStores(useMainStore),
    messages() {
      return this.mainStore.alertMessages;
    },
    allMuted() {
      return !this.messages.find((message) => message.muted === false);
    },
  },
  methods: {
    updateAlert(alert, shouldMute) {
      const key = localStoreKey(alert);
      log.info(`${shouldMute ? 'Muting' : 'Unmuting'} alert: `, alert.id);
      if (shouldMute) localStore.set(key, true);
      else localStore.remove(key);
      alert.muted = shouldMute;
      return alert;
    },
    onMuteClick(id) {
      const newMessages = this.messages.reduce((acc, alert) => {
        if (alert.id === id) {
          const updatedAlert = this.updateAlert(alert, !alert.muted);
          acc.push(updatedAlert);
        } else acc.push(alert);
        return acc;
      }, []);
      this.mainStore.alertMessages = newMessages;
    },
    onMuteAll(shouldMute) {
      const newMessages = this.messages.reduce((acc, alert) => {
        if (alert.muted !== shouldMute) {
          const updatedAlert = this.updateAlert(alert, shouldMute);
          acc.push(updatedAlert);
        } else acc.push(alert);
        return acc;
      }, []);
      this.mainStore.alertMessages = newMessages;
    },
    /**
     * Dismiss the backend event by deleting the event by id
     * @param id
     * @return {Promise}
     */
    dismissBackendEvent(id) {
      return dashboardBffClient.bff
        .bffDeleteEvent(id)
        .then(() => {
          checkAll();
        })
        .catch((error) => {
          log.warn(`Error deleting backend event "${id}"`, error);
          // If the event can't be found, it may have been deleted already. So we refresh the events.
          if (error?.response?.status === 404) {
            checkAll();
          }
        });
    },
    onButtonClick(alert) {
      if (alert.button.dismiss) {
        this.dismissBackendEvent(alert.id);
        return;
      }
      this.$emit('close');
    },
  },
};
</script>
